export const PRODUCT_IMAGE_FIELDS = ['imagen1', 'imagen2', 'imagen3', 'imagen4'];

const DEFAULT_PRODUCT_IMAGE_OPTIONS = {
    maxWidth: 1200,
    maxHeight: 1200,
    quality: 0.82,
    preferredType: 'image/webp',
};

const SKIP_IMAGE_TYPES = ['image/gif', 'image/svg+xml'];

const extensionByType = {
    'image/webp': 'webp',
    'image/jpeg': 'jpg',
    'image/png': 'png',
};

export const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
    const units = ['B', 'KB', 'MB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    const decimals = unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(decimals)} ${units[unitIndex]}`;
};

export const optimizeProductImageFile = async (file, options = {}) => {
    const settings = { ...DEFAULT_PRODUCT_IMAGE_OPTIONS, ...options };

    if (!file || !(file instanceof File) || !file.type?.startsWith('image/')) {
        return { file, changed: false, originalSize: file?.size || 0, optimizedSize: file?.size || 0 };
    }

    if (SKIP_IMAGE_TYPES.includes(file.type)) {
        return { file, changed: false, originalSize: file.size, optimizedSize: file.size };
    }

    try {
        const image = await loadImage(file);
        const targetSize = getContainSize(image.width, image.height, settings.maxWidth, settings.maxHeight);

        if (!targetSize.width || !targetSize.height) {
            return { file, changed: false, originalSize: file.size, optimizedSize: file.size };
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetSize.width;
        canvas.height = targetSize.height;

        const context = canvas.getContext('2d');
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        revokeObjectUrl(image.src);

        const blob = await canvasToBestBlob(canvas, settings.preferredType, settings.quality);

        if (!blob || blob.size >= file.size) {
            return {
                file,
                changed: targetSize.width !== image.width || targetSize.height !== image.height,
                originalSize: file.size,
                optimizedSize: file.size,
                width: image.width,
                height: image.height,
                outputWidth: targetSize.width,
                outputHeight: targetSize.height,
            };
        }

        const optimizedFile = new File(
            [blob],
            renameImageFile(file.name, blob.type),
            { type: blob.type, lastModified: Date.now() }
        );

        return {
            file: optimizedFile,
            changed: true,
            originalSize: file.size,
            optimizedSize: optimizedFile.size,
            width: image.width,
            height: image.height,
            outputWidth: targetSize.width,
            outputHeight: targetSize.height,
        };
    } catch (error) {
        console.error('No se pudo optimizar la imagen:', error);
        return { file, changed: false, originalSize: file.size, optimizedSize: file.size, error };
    }
};

export const optimizeFormDataProductImages = async (formData, fields = PRODUCT_IMAGE_FIELDS) => {
    const results = [];

    for (const field of fields) {
        const value = formData.get(field);

        if (!(value instanceof File) || !value.name || value.size === 0) {
            continue;
        }

        const result = await optimizeProductImageFile(value);
        formData.set(field, result.file);
        results.push({ field, ...result });
    }

    return results;
};

export const getImageOptimizationSummary = (results) => {
    const optimized = results.filter(item => item.changed && item.optimizedSize < item.originalSize);
    const savedBytes = optimized.reduce((total, item) => total + (item.originalSize - item.optimizedSize), 0);

    return {
        count: optimized.length,
        savedBytes,
        text: optimized.length
            ? `${optimized.length} imagen(es) optimizada(s). Peso reducido: ${formatBytes(savedBytes)}.`
            : '',
    };
};

const loadImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => {
        revokeObjectUrl(url);
        reject(new Error('No se pudo leer la imagen seleccionada.'));
    };

    image.src = url;
});

const getContainSize = (width, height, maxWidth, maxHeight) => {
    const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
    return {
        width: Math.max(1, Math.round(width * ratio)),
        height: Math.max(1, Math.round(height * ratio)),
    };
};

const canvasToBestBlob = async (canvas, preferredType, quality) => {
    const preferredBlob = await canvasToBlob(canvas, preferredType, quality);

    if (preferredBlob?.type === preferredType) {
        return preferredBlob;
    }

    return canvasToBlob(canvas, 'image/jpeg', quality);
};

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
});

const renameImageFile = (name, mimeType) => {
    const extension = extensionByType[mimeType] || 'jpg';
    const cleanName = String(name || 'producto')
        .replace(/\.[a-z0-9]+$/i, '')
        .replace(/[^\w.-]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'producto';

    return `${cleanName}.${extension}`;
};

const revokeObjectUrl = (url) => {
    if (typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
    }
};
